import React, { useState, useEffect, useRef } from 'react';

const API     = 'https://api.nowatt.app';
const APP_URL = 'https://app.nowatt.app';

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity:    visible ? 1 : 0,
      transform:  visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
}

function Icon({ name, size = 22, color = '#C8F135' }) {
  const icons = {
    plug: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a1 1 0 0 0-1 1v5a7 7 0 0 0 14 0V9a1 1 0 0 0-1-1Z"/></svg>,
    brain: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/></svg>,
    zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    bot: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="10" x="3" y="11" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  };
  return icons[name] || null;
}

// ── Couleurs ──
const G = {
  bg:    '#06080C',
  lime:  '#C8F135',
  text:  '#EEF2FF',
  muted: 'rgba(238,242,255,0.5)',
  card:  '#0D1117',
  border:'rgba(238,242,255,0.08)',
  title: { fontFamily: "'Lexend', sans-serif" },
  body:  { fontFamily: "'Plus Jakarta Sans', sans-serif" },
};

export default function Landing() {
  const [email,     setEmail]     = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [count,     setCount]     = useState(null); // nombre réel d'inscrits, affiché seulement une fois reçu du serveur
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    fetch(`${API}/waitlist/count`).then(r => r.json()).then(d => { if (d.count) setCount(d.count); }).catch(() => {});
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setLoading(true);
    try {
      await fetch(`${API}/waitlist/join`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      setSubmitted(true); setCount(c => (c == null ? c : c + 1));
    } catch {}
    setLoading(false);
  };

  return (
    <div style={{ background: G.bg, color: G.text, minHeight: '100vh', ...G.body, overflowX: 'hidden' }}>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
        input::placeholder { color: rgba(238,242,255,0.3); }
        input:focus { outline: none; }
        @keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.8)} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes glow-pulse { 0%,100%{box-shadow:0 0 40px rgba(200,241,53,0.3)} 50%{box-shadow:0 0 70px rgba(200,241,53,0.55)} }
        @media (max-width: 640px) {
          .hero-section  { padding-top:72px!important; padding-bottom:36px!important; min-height:auto!important; justify-content:flex-start!important; }
          .hero-title    { font-size:1.75rem!important; }
          .steps-grid    { grid-template-columns:1fr!important; gap:10px!important; }
          .pain-grid     { grid-template-columns:1fr!important; gap:10px!important; }
          .nav-links     { display:none!important; }
          .section-pad   { padding:52px 18px!important; }
          .lime-banner   { padding:52px 18px!important; }
          .price-num     { font-size:3.2rem!important; }
          .stats-banner  { padding:32px 18px!important; }
          .stats-banner-grid { grid-template-columns:repeat(3,1fr)!important; gap:8px!important; }
          .footer-inner  { flex-direction:column!important; align-items:flex-start!important; gap:14px!important; }
          .vs-table-feat { font-size:0.72rem!important; padding:10px 10px!important; }
          .vs-table-cell { padding:10px 10px!important; }
          .example-pad   { padding:18px 18px!important; }
        }
      `}</style>

      {/* ══ NAV ══ */}
      <nav style={{
        position:'fixed', top:0, left:0, right:0, zIndex:100,
        padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between',
        background:'rgba(6,8,12,0.92)', backdropFilter:'blur(16px)', borderBottom:`1px solid ${G.border}`,
      }}>
        <img src="/Logo_nowatt_Black.png" alt="Nowatt" style={{ width:40, height:40, objectFit:'contain', mixBlendMode:'screen' }} />
        <div className="nav-links" style={{ display:'flex', gap:10, alignItems:'center' }}>
          <a href={APP_URL} style={{ color:G.muted, textDecoration:'none', fontSize:'0.88rem', padding:'8px 14px', borderRadius:8, border:`1px solid ${G.border}`, ...G.body }}>Connexion</a>
          <a href={APP_URL} style={{ background:G.lime, color:G.bg, textDecoration:'none', fontSize:'0.88rem', fontWeight:700, padding:'10px 20px', borderRadius:10, ...G.title }}>Commencer ⚡</a>
        </div>
      </nav>

      {/* ══ HERO — fond noir ══ */}
      <section className="hero-section" style={{
        minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        padding:'120px 20px 80px', textAlign:'center', position:'relative',
      }}>
        <div style={{ position:'absolute', top:'30%', left:'50%', transform:'translate(-50%,-50%)', width:600, height:400, background:'radial-gradient(ellipse, rgba(200,241,53,0.07) 0%, transparent 70%)', pointerEvents:'none' }} />

        <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(200,241,53,0.1)', border:'1px solid rgba(200,241,53,0.2)', borderRadius:100, padding:'6px 16px', marginBottom:24, fontSize:'0.72rem', color:G.lime, fontWeight:600, letterSpacing:0.5, animation:'float 4s ease-in-out infinite', ...G.body }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:G.lime, animation:'pulse-dot 2s ease-in-out infinite' }} />
          BÊTA EN COURS DE LANCEMENT
        </div>

        <h1 className="hero-title" style={{ ...G.title, fontSize:'clamp(1.75rem, 4vw, 3.6rem)', fontWeight:900, lineHeight:1.08, margin:'0 0 6px', maxWidth:760 }}>
         Vous payez trop cher votre électricité ?
        </h1>
        <h1 className="hero-title" style={{ ...G.title, fontSize:'clamp(1.75rem, 4vw, 3.6rem)', fontWeight:900, lineHeight:1.08, margin:'0 0 22px', color:G.lime }}>
          on s'en occupe.
        </h1>

        <div style={{ background:'rgba(238,242,255,0.04)', border:`1px solid ${G.border}`, borderRadius:12, padding:'11px 18px', marginBottom:20, fontSize:'0.85rem', color:G.muted, maxWidth:480, ...G.body }}>
          📊 <strong style={{ color:G.text }}>70% des Français</strong> n'ont jamais changé de fournisseur. Ils perdent en moyenne <strong style={{ color:G.lime }}>170€/an</strong> sans le savoir.
        </div>

        <p style={{ fontSize:'1rem', color:G.muted, maxWidth:500, lineHeight:1.6, margin:'0 0 32px', ...G.body }}>
          Nowatt connecte votre compteur Linky, surveille les tarifs et prépare votre changement vers le moins cher —{' '}
          <strong style={{ color:G.text }}>vous validez en un clic.</strong>
        </p>

        <a href={APP_URL} style={{ display:'inline-block', background:G.lime, color:G.bg, textDecoration:'none', fontWeight:800, fontSize:'1.05rem', padding:'16px 36px', borderRadius:14, marginBottom:10, ...G.title, animation:'glow-pulse 3s ease-in-out infinite', transition:'transform 0.15s' }}
          onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; }}>
          Voir mes économies — gratuit ⚡
        </a>
        <p style={{ margin:0, fontSize:'0.7rem', color:'rgba(238,242,255,0.3)', ...G.body }}>Sans engagement · Données 100% privées</p>

        {count != null && (
        <div style={{ marginTop:24, display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ display:'flex' }}>
            {['#4ade80','#60a5fa','#f472b6','#facc15'].map((c,i) => (
              <div key={i} style={{ width:26, height:26, borderRadius:'50%', background:c, border:'2px solid #06080C', marginLeft:i ? -8 : 0 }} />
            ))}
          </div>
          <span style={{ fontSize:'0.8rem', color:G.muted, ...G.body }}><strong style={{ color:G.text }}>{count}</strong> foyers déjà inscrits</span>
        </div>
        )}
      </section>

      {/* ══ BANNIÈRE STATS — fond lime vert ══ */}
      <div className="stats-banner" style={{ background:G.lime, padding:'40px 24px' }}>
        <div className="stats-banner-grid" style={{ maxWidth:800, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, textAlign:'center' }}>
          {[
            { val:'35M',   label:'compteurs Linky en France' },
            { val:'170€',  label:'perdus/an en moyenne' },
            { val:'25+',   label:'fournisseurs comparés' },
          ].map((s,i) => (
            <div key={i}>
              <p style={{ margin:'0 0 4px', ...G.title, fontSize:'clamp(1.8rem, 5vw, 2.8rem)', fontWeight:900, color:G.bg }}>{s.val}</p>
              <p style={{ margin:0, ...G.body, fontSize:'0.82rem', color:'rgba(6,8,12,0.65)', fontWeight:600 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ══ TICKER — fond sombre ══ */}
      <div style={{ overflow:'hidden', borderBottom:`1px solid ${G.border}`, padding:'13px 0', background:'rgba(200,241,53,0.03)' }}>
        <div style={{ display:'flex', gap:52, animation:'ticker 20s linear infinite', width:'max-content' }}>
          {['⚡ 35M de compteurs Linky','💰 170€ perdus/an','🤖 25+ fournisseurs','🔌 Tes données de compteur','🔄 Switch sans coupure',
            '⚡ 35M de compteurs Linky','💰 170€ perdus/an','🤖 25+ fournisseurs','🔌 Tes données de compteur','🔄 Switch sans coupure'].map((s,i) => (
            <span key={i} style={{ ...G.body, fontSize:'0.77rem', color:G.muted, whiteSpace:'nowrap', fontWeight:600 }}>{s}</span>
          ))}
        </div>
      </div>

      {/* ══ COMMENT ÇA MARCHE — fond sombre ══ */}
      <section className="section-pad" style={{ maxWidth:900, margin:'0 auto', padding:'90px 20px' }}>
        <Reveal>
          <p style={{ ...G.body, color:G.lime, fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:2, textAlign:'center', marginBottom:10, fontWeight:700 }}>Comment ça marche</p>
          <h2 style={{ ...G.title, fontSize:'clamp(1.5rem, 4vw, 2.8rem)', fontWeight:800, textAlign:'center', margin:'0 0 48px', lineHeight:1.2 }}>Trois étapes. Un seul clic.</h2>
        </Reveal>
        <div className="steps-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
          {[
            { num:'01', icon:'plug',  title:'Vous connectez votre compteur', body:'Vous donnez votre accord depuis votre espace client Enedis. On ne lit vos données de consommation qu\'avec votre consentement.' },
            { num:'02', icon:'brain', title:'On analyse en continu',      body:'Notre agent IA compare votre conso 24h/24 aux offres de 25+ fournisseurs.' },
            { num:'03', icon:'zap',   title:'Vous changez en un clic',     body:'Dès qu\'une meilleure offre apparaît, on prépare tout. Vous validez, sans coupure.' },
          ].map((step,i) => (
            <Reveal key={step.num} delay={i*100}>
              <div style={{ background:G.card, borderRadius:18, padding:22, border:`1px solid ${G.border}`, position:'relative', overflow:'hidden', display:'flex', flexDirection:'column', height:'100%' }}>
                <div style={{ position:'absolute', top:12, right:16, ...G.title, fontSize:'3rem', fontWeight:900, color:'rgba(200,241,53,0.05)', lineHeight:1 }}>{step.num}</div>
                <div style={{ width:40, height:40, borderRadius:10, background:'rgba(200,241,53,0.1)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14, flexShrink:0 }}>
                  <Icon name={step.icon} size={18} />
                </div>
                <h3 style={{ ...G.title, margin:'0 0 8px', fontSize:'0.95rem', fontWeight:700 }}>{step.title}</h3>
                <p style={{ margin:0, color:G.muted, fontSize:'0.82rem', lineHeight:1.6, flex:1, ...G.body }}>{step.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ══ BANNIÈRE LIME — "pourquoi tu perds" ══ */}
      <section className="lime-banner" style={{ background:G.lime, padding:'80px 20px' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <Reveal>
            <p style={{ ...G.body, color:'rgba(6,8,12,0.55)', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:2, textAlign:'center', marginBottom:10, fontWeight:700 }}>Le problème</p>
            <h2 style={{ ...G.title, fontSize:'clamp(1.5rem, 4vw, 2.6rem)', fontWeight:900, textAlign:'center', margin:'0 0 12px', color:G.bg }}>
              Le marché de l'énergie est conçu pour que vous payiez trop.
            </h2>
            <p style={{ textAlign:'center', color:'rgba(6,8,12,0.6)', margin:'0 0 44px', maxWidth:440, marginLeft:'auto', marginRight:'auto', lineHeight:1.6, fontSize:'0.9rem', ...G.body }}>
              Les fournisseurs misent sur votre inaction. Nowatt retourne ça contre eux.
            </p>
          </Reveal>
          <div className="pain-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
            {[
              { icon:'clock',  title:'Les tarifs changent sans vous prévenir',    body:'Sans surveillance, vous restez bloqué sur une offre devenue chère.' },
              { icon:'shield', title:'Changer, c\'est un parcours du combattant', body:'70% des Français ne l\'ont jamais fait. Faute de temps, faute d\'info.' },
              { icon:'bot',    title:'Nowatt le fait pour vous, pour toujours',   body:'Une seule inscription. L\'agent surveille et agit sans vous déranger.' },
            ].map((item,i) => (
              <Reveal key={item.title} delay={i*80}>
                <div style={{ background:'rgba(6,8,12,0.1)', borderRadius:14, padding:20, border:'1px solid rgba(6,8,12,0.12)', display:'flex', flexDirection:'column', height:'100%' }}>
                  <div style={{ width:38, height:38, borderRadius:9, background:'rgba(6,8,12,0.12)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14, flexShrink:0 }}>
                    <Icon name={item.icon} size={17} color={G.bg} />
                  </div>
                  <h3 style={{ ...G.title, margin:'0 0 8px', fontSize:'0.92rem', fontWeight:700, color:G.bg, lineHeight:1.3 }}>{item.title}</h3>
                  <p style={{ margin:0, color:'rgba(6,8,12,0.65)', fontSize:'0.82rem', lineHeight:1.6, flex:1, ...G.body }}>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ EXEMPLE — fond sombre ══ */}
      <section className="section-pad" style={{ maxWidth:720, margin:'0 auto', padding:'90px 20px' }}>
        <Reveal>
          <p style={{ ...G.body, color:G.lime, fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:2, textAlign:'center', marginBottom:10, fontWeight:700 }}>Exemple concret</p>
          <h2 style={{ ...G.title, fontSize:'clamp(1.3rem, 3.5vw, 2.2rem)', fontWeight:800, textAlign:'center', margin:'0 0 32px' }}>
            Un foyer comme le vôtre, 136€ net économisés/an
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <div style={{ background:G.card, borderRadius:18, border:`1px solid ${G.border}`, overflow:'hidden' }}>
            <div className="example-pad" style={{ padding:'20px 24px', borderBottom:`1px solid ${G.border}` }}>
              <p style={{ margin:'0 0 12px', color:G.muted, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:1, ...G.body }}>Situation actuelle</p>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <p style={{ margin:0, fontWeight:700, ...G.title }}>EDF — Tarif Bleu</p>
                  <p style={{ margin:'2px 0 0', color:G.muted, fontSize:'0.75rem', ...G.body }}>Fournisseur historique</p>
                </div>
                <div style={{ textAlign:'right' }}>
                  <p style={{ margin:0, fontWeight:800, fontSize:'1.2rem', ...G.title }}>1 840€</p>
                  <p style={{ margin:'2px 0 0', color:G.muted, fontSize:'0.72rem', ...G.body }}>par an</p>
                </div>
              </div>
            </div>
            <div style={{ padding:'12px 24px', background:'rgba(200,241,53,0.07)', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:26, height:26, borderRadius:7, background:'rgba(200,241,53,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon name="zap" size={13} />
              </div>
              <div>
                <p style={{ margin:0, fontSize:'0.78rem', fontWeight:700, color:G.lime, ...G.title }}>Nowatt détecte une meilleure offre</p>
                <p style={{ margin:0, fontSize:'0.68rem', color:G.muted, ...G.body }}>25 fournisseurs analysés en temps réel</p>
              </div>
            </div>
            <div className="example-pad" style={{ padding:'20px 24px', background:'linear-gradient(135deg,rgba(13,26,0,0.8),rgba(8,16,0,0.8))' }}>
              <p style={{ margin:'0 0 12px', color:'rgba(200,241,53,0.6)', fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:1, ...G.body }}>Offre recommandée par Nowatt</p>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div>
                  <p style={{ margin:0, fontWeight:700, ...G.title }}>Octopus Energy</p>
                  <p style={{ margin:'2px 0 0', color:'rgba(200,241,53,0.6)', fontSize:'0.75rem', ...G.body }}>✦ Sélection IA Nowatt</p>
                </div>
                <div style={{ textAlign:'right' }}>
                  <p style={{ margin:0, fontWeight:800, fontSize:'1.2rem', color:G.lime, ...G.title }}>1 620€</p>
                  <p style={{ margin:'2px 0 0', color:'rgba(200,241,53,0.5)', fontSize:'0.72rem', ...G.body }}>par an</p>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                {[
                  { label:'Économies brutes',  value:'220€/an',   color:G.lime },
                  { label:'Abonnement Nowatt', value:'83,88€/an', color:G.muted },
                  { label:'Gain net',          value:'136€/an',   color:'#4ade80' },
                ].map(s => (
                  <div key={s.label} style={{ background:'rgba(238,242,255,0.04)', borderRadius:9, padding:'10px 6px', textAlign:'center' }}>
                    <p style={{ margin:'0 0 3px', fontSize:'0.6rem', color:G.muted, ...G.body }}>{s.label}</p>
                    <p style={{ margin:0, fontWeight:700, fontSize:'0.84rem', color:s.color, ...G.title }}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ══ VS CONCURRENTS — fond sombre ══ */}
      <section className="section-pad" style={{ padding:'0 20px 90px' }}>
        <div style={{ maxWidth:800, margin:'0 auto' }}>
          <Reveal>
            <h2 style={{ ...G.title, fontSize:'clamp(1.3rem, 3.5vw, 2.2rem)', fontWeight:800, textAlign:'center', margin:'0 0 8px' }}>Nowatt vs les comparateurs</h2>
            <p style={{ textAlign:'center', color:G.muted, margin:'0 0 32px', fontSize:'0.86rem', ...G.body }}>LeLynx, Kelwatt comparent. <strong style={{ color:G.text }}>Nowatt agit.</strong></p>
          </Reveal>
          <Reveal delay={80}>
            <div style={{ background:G.card, borderRadius:18, border:`1px solid ${G.border}`, overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', borderBottom:`1px solid ${G.border}` }}>
                <div style={{ padding:'14px 16px' }} />
                <div style={{ padding:'14px 16px', borderLeft:`1px solid ${G.border}`, textAlign:'center' }}>
                  <p style={{ margin:0, fontSize:'0.75rem', color:G.muted, fontWeight:600, ...G.body }}>LeLynx / Kelwatt</p>
                  <p style={{ margin:'2px 0 0', fontSize:'0.65rem', color:'rgba(238,242,255,0.25)', ...G.body }}>Passifs</p>
                </div>
                <div style={{ padding:'14px 16px', borderLeft:`1px solid ${G.border}`, textAlign:'center', background:'rgba(200,241,53,0.06)' }}>
                  <p style={{ margin:0, fontSize:'0.75rem', color:G.lime, fontWeight:700, ...G.title }}>Nowatt</p>
                  <p style={{ margin:'2px 0 0', fontSize:'0.65rem', color:'rgba(200,241,53,0.5)', ...G.body }}>Agent IA</p>
                </div>
              </div>
              {[
                { feature:'Compare les offres',         comp:true,  now:true },
                { feature:'Surveillance en temps réel', comp:false, now:true },
                { feature:'Switch en un clic',          comp:false, now:true },
                { feature:'Données réelles du compteur', comp:false, now:true },
                { feature:'Alertes et rapports',        comp:false, now:true },
                { feature:'Veille à votre place',          comp:false, now:true },
              ].map((row,i) => (
                <div key={row.feature} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', borderBottom:i<5?`1px solid ${G.border}`:'none' }}>
                  <div className="vs-table-feat" style={{ padding:'12px 16px', fontSize:'0.8rem', display:'flex', alignItems:'center', ...G.body }}>{row.feature}</div>
                  <div className="vs-table-cell" style={{ padding:'12px 16px', borderLeft:`1px solid ${G.border}`, textAlign:'center', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {row.comp ? <span style={{ color:'#4ade80', fontSize:'1rem' }}>✓</span> : <span style={{ color:'rgba(238,242,255,0.15)', fontSize:'1rem' }}>✗</span>}
                  </div>
                  <div className="vs-table-cell" style={{ padding:'12px 16px', borderLeft:`1px solid ${G.border}`, textAlign:'center', display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(200,241,53,0.03)' }}>
                    <span style={{ color:G.lime, fontSize:'1rem' }}>✓</span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ BANNIÈRE LIME — TARIF ══ */}
      <section className="lime-banner" style={{ background:G.lime, padding:'80px 20px' }}>
        <div style={{ maxWidth:520, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <p style={{ ...G.body, color:'rgba(6,8,12,0.55)', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:2, margin:'0 0 10px', fontWeight:700 }}>Tarif</p>
            <h2 style={{ ...G.title, fontSize:'clamp(1.5rem, 4vw, 2.2rem)', fontWeight:900, color:G.bg, margin:'0 0 6px' }}>Simple, transparent, sans surprise.</h2>
            <p style={{ ...G.body, color:'rgba(6,8,12,0.55)', fontSize:'0.88rem', margin:'0 0 28px' }}>Vous récupérez en moyenne 2× votre mise.</p>

            {/* Prix */}
            <div style={{ marginBottom:6 }}>
              <span className="price-num" style={{ ...G.title, fontSize:'4rem', fontWeight:900, color:G.bg }}>5,99€</span>
              <span style={{ color:'rgba(6,8,12,0.55)', fontSize:'1rem', ...G.body }}>/mois</span>
            </div>
            <p style={{ ...G.body, color:'rgba(6,8,12,0.5)', fontSize:'0.76rem', margin:'0 0 28px' }}>Résiliable à tout moment · Sans engagement</p>

            {/* Features */}
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:32, textAlign:'left' }}>
              {[
                'Connexion à votre compteur Linky, avec votre accord',
                'Surveillance 24h/24 des tarifs fournisseurs',
                'Switch en un clic vers le meilleur prix',
                'Dashboard de suivi en temps réel',
                'Rapport mensuel d\'économies',
                'Support client réactif',
              ].map(feature => (
                <div key={feature} style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:20, height:20, borderRadius:'50%', background:'rgba(6,8,12,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Icon name="check" size={11} color={G.bg} />
                  </div>
                  <span style={{ fontSize:'0.84rem', color:'rgba(6,8,12,0.75)', ...G.body }}>{feature}</span>
                </div>
              ))}
            </div>

            <a href={APP_URL} style={{ display:'block', background:G.bg, color:G.lime, textDecoration:'none', fontWeight:800, fontSize:'1rem', padding:'15px', borderRadius:12, marginBottom:10, ...G.title }}>
              Commencer maintenant ⚡
            </a>
            <p style={{ margin:0, fontSize:'0.7rem', color:'rgba(6,8,12,0.45)', ...G.body }}>
              Les 100 premiers inscrits : <strong style={{ color:G.bg }}>3 mois offerts</strong>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA FINAL — fond sombre ══ */}
      <section className="section-pad" style={{ padding:'90px 20px 100px' }}>
        <div style={{ maxWidth:600, margin:'0 auto', textAlign:'center' }}>
          <Reveal>
            <h2 style={{ ...G.title, fontSize:'clamp(1.7rem, 5vw, 3rem)', fontWeight:900, margin:'0 0 10px', lineHeight:1.1 }}>
              Arrêtez de payer<br /><span style={{ color:G.lime }}>trop cher.</span>
            </h2>
            <p style={{ color:G.muted, margin:'0 0 28px', fontSize:'0.92rem', lineHeight:1.6, ...G.body }}>
              Rejoignez les premiers foyers qui laissent Nowatt gérer leur facture d'électricité.
            </p>
            <a href={APP_URL} style={{ display:'block', background:G.lime, color:G.bg, textDecoration:'none', fontWeight:800, fontSize:'1rem', padding:'15px', borderRadius:13, marginBottom:14, ...G.title, maxWidth:400, margin:'0 auto 14px', boxShadow:'0 0 50px rgba(200,241,53,0.22)' }}>
              Je commence maintenant — c'est gratuit ⚡
            </a>
            {!submitted ? (
              <form onSubmit={handleSignup} style={{ display:'flex', gap:8, maxWidth:400, margin:'0 auto' }}>
                <input type="email" placeholder="Être notifié par email..." value={email} onChange={e => setEmail(e.target.value)}
                  style={{ flex:1, background:G.card, border:`1px solid ${G.border}`, borderRadius:10, padding:'12px 14px', color:G.text, fontSize:'0.86rem', ...G.body }}
                />
                <button type="submit" disabled={loading} style={{ background:'transparent', border:`1px solid ${G.border}`, borderRadius:10, padding:'12px 16px', color:G.muted, cursor:'pointer', fontSize:'0.82rem', whiteSpace:'nowrap', ...G.body }}>
                  {loading ? '...' : 'Me prévenir'}
                </button>
              </form>
            ) : (
              <div style={{ background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:'12px 18px', color:'#4ade80', fontSize:'0.86rem', ...G.body }}>
                ✓ Parfait ! Vous serez parmi les premiers contactés.
              </div>
            )}
            <p style={{ margin:'12px 0 0', fontSize:'0.7rem', color:'rgba(238,242,255,0.25)', ...G.body }}>
              {count != null ? `${count} personnes déjà inscrites · ` : ''}Données 100% privées
            </p>
          </Reveal>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ borderTop:`1px solid ${G.border}`, padding:'26px 20px' }}>
        <div className="footer-inner" style={{ maxWidth:1100, margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14 }}>
          <img src="/Logo_nowatt_Black.png" alt="Nowatt" style={{ width:32, height:32, objectFit:'contain', mixBlendMode:'screen' }} />
          <div style={{ display:'flex', gap:18, flexWrap:'wrap' }}>
            {[{ label:'Mentions légales', href:'/legal' },{ label:'Confidentialité', href:'/legal' },{ label:'Contact', href:'mailto:contact@nowatt.app' }].map(link => (
              <a key={link.label} href={link.href} style={{ color:G.muted, textDecoration:'none', fontSize:'0.78rem', ...G.body }}>{link.label}</a>
            ))}
          </div>
          <p style={{ margin:0, color:'rgba(238,242,255,0.2)', fontSize:'0.72rem', ...G.body }}>© 2026 Nowatt SAS</p>
        </div>
      </footer>
    </div>
  );
}
